import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Typography,
  Row,
  Col,
  Tabs,
  Tag,
  Upload
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  TrophyOutlined,
  CloseOutlined,
  UploadOutlined,
  CopyOutlined,
  SendOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;

const BASE_URL = 'https://ds2.cn/sh';

interface EvolutionCardType {
  id: number;
  code: string;
  cardImage: string;
  created: string;
  updated: string;
  instances: EvolutionCardInstance[];
}

interface EvolutionCardInstance {
  id: number;
  hash: string;
  isEnhanced: boolean;
  hasVoted: boolean;
  created: string;
  voterEnhancements?: VoterEnhancement[];
}

interface VoterEnhancement {
  id: number;
  voteId: number;
  enhancementLevel: 'SSS' | 'S' | 'A';
  healthBonus: number;
  speedBonus: number;
  skillBonus: number;
  votedForWinner: boolean;
  created: string;
}

interface Vote {
  id: number;
  startTime: string;
  endTime: string;
  currentStage: number;
  status: 'active' | 'closed';
  created: string;
}

interface OcCard {
  id: number;
  code: string;
  name: string;
  backgroundImage: string;
  prototypeSketch: string;
  prototypeImage: string;
  created: string;
  instances: OcCardInstance[];
}

interface OcCardInstance {
  id: number;
  hash: string;
  aiGeneratedImage: string;
  finalImage: string;
  aiImagePrompt: string;
  newSkillPrompt: string;
  newSkillName: string;
  newSkillDescription: string;
  chineseName: string;
  italianName: string;
  userName: string;
  userPhone: string;
  userAddress: string;
  expressTrackingNumber: string;
  hasAppliedExchange: boolean;
  created: string;
  prototype?: {
    id: number;
    name: string;
  };
}

const AiShanhaijingAdmin: React.FC = () => {
  const [evolutionCardTypes, setEvolutionCardTypes] = useState<EvolutionCardType[]>([]);
  const [ocCards, setOcCards] = useState<OcCard[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [voteCandidates, setVoteCandidates] = useState<any[]>([]);
  const [voteCategoriesGroups, setVoteCategoriesGroups] = useState<any[]>([]);
  const [selectedVote, setSelectedVote] = useState<Vote | null>(null);
  const [winnerCards, setWinnerCards] = useState<OcCardInstance[]>([]);
  const [winnerCardFilter, setWinnerCardFilter] = useState<'all' | 'shipped' | 'unshipped'>('all');
  const [loading, setLoading] = useState(false);
  
  // Champion selection modal states
  const [championModalVisible, setChampionModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [championForm] = Form.useForm();
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Modal states
  const [evolutionTypeModalVisible, setEvolutionTypeModalVisible] = useState(false);
  const [generateEvolutionModalVisible, setGenerateEvolutionModalVisible] = useState(false);
  const [voteModalVisible, setVoteModalVisible] = useState(false);
  const [ocCardModalVisible, setOcCardModalVisible] = useState(false);
  const [generateOcModalVisible, setGenerateOcModalVisible] = useState(false);
  const [voteCategoriesModalVisible, setVoteCategoriesModalVisible] = useState(false);
  const [shippingModalVisible, setShippingModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [batchGenerateType, setBatchGenerateType] = useState<'evolution' | 'oc'>('evolution');
  
  const [selectedEvolutionType, setSelectedEvolutionType] = useState<EvolutionCardType | null>(null);
  const [selectedOcCard, setSelectedOcCard] = useState<OcCard | null>(null);
  const [selectedWinnerCard, setSelectedWinnerCard] = useState<OcCardInstance | null>(null);
  
  const [evolutionTypeForm] = Form.useForm();
  const [generateEvolutionForm] = Form.useForm();
  const [voteForm] = Form.useForm();
  const [ocCardForm] = Form.useForm();
  const [generateOcForm] = Form.useForm();
  const [shippingForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  // 从URL获取当前活跃的标签页
  const getActiveTab = () => {
    const hash = window.location.hash; // 例如：#/tools/ai_shanhaijing_admin?tab=evolution-cards
    const queryIndex = hash.indexOf('?');
    if (queryIndex > -1) {
      const queryString = hash.substring(queryIndex + 1);
      const urlParams = new URLSearchParams(queryString);
      const tab = urlParams.get('tab');
      if (tab === 'evolution-cards' || tab === 'votes' || tab === 'oc-cards' || tab === 'winner-cards') {
        return tab;
      }
    }
    return 'evolution-cards'; // 默认
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  useEffect(() => {
    fetchEvolutionCardTypes();
    fetchOcCards();
    fetchVotes();
    fetchWinnerCards();
    
    // 抑制ResizeObserver错误
    const resizeObserverErrorHandler = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications' || 
          e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
        return false;
      }
    };
    
    window.addEventListener('error', resizeObserverErrorHandler);
    
    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
    };
  }, []);


  // 处理标签页切换 - 使用页面刷新规避ResizeObserver问题
  const handleTabChange = (key: string) => {
    // 更新URL然后刷新页面
    window.history.pushState(null, '', `/#/tools/ai_shanhaijing_admin?tab=${key}`);
    window.location.reload();
  };

  const fetchEvolutionCardTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/evolution-card-types');
      const data = await response.json();
      if (data.success) {
        setEvolutionCardTypes(data.data || []);
      } else {
        message.error(data.message || '获取进化卡种类失败');
      }
    } catch (error) {
      message.error('获取进化卡种类失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchOcCards = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/oc-cards');
      const data = await response.json();
      if (data.success) {
        setOcCards(data.data || []);
      } else {
        message.error(data.message || '获取OC卡失败');
      }
    } catch (error) {
      message.error('获取OC卡失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchVotes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/votes');
      const data = await response.json();
      if (data.success) {
        setVotes(data.data || []);
      } else {
        message.error(data.message || '获取投票失败');
      }
    } catch (error) {
      message.error('获取投票失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoteCandidates = async (voteId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ai-shanhaijing/votes/${voteId}/oc-cards/1/candidates`);
      const data = await response.json();
      if (data.success) {
        setVoteCandidates(data.data || []);
      } else {
        message.error(data.message || '获取投票候选人失败');
      }
    } catch (error) {
      message.error('获取投票候选人失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoteCategoriesGroups = async (voteId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ai-shanhaijing/votes/${voteId}/categories-groups`);
      const data = await response.json();
      if (data.success) {
        setVoteCategoriesGroups(data.data || []);
      } else {
        message.error(data.message || '获取投票分类数据失败');
      }
    } catch (error) {
      message.error('获取投票分类数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchWinnerCards = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/winner-cards');
      const data = await response.json();
      if (data.success) {
        setWinnerCards(data.data || []);
      } else {
        message.error(data.message || '获取获胜卡牌失败');
      }
    } catch (error) {
      message.error('获取获胜卡牌失败');
    } finally {
      setLoading(false);
    }
  };

  const updateShippingStatus = async (cardInstanceId: number, expressTrackingNumber?: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ai-shanhaijing/oc-card-instances/${cardInstanceId}/shipping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ expressTrackingNumber })
      });

      const data = await response.json();
      if (data.success) {
        message.success('发货状态更新成功');
        fetchWinnerCards(); // 重新获取数据
        setShippingModalVisible(false);
        shippingForm.resetFields();
      } else {
        message.error(data.message || '更新发货状态失败');
      }
    } catch (error) {
      message.error('更新发货状态失败');
    } finally {
      setLoading(false);
    }
  };

  const copyAddressToClipboard = async (card: OcCardInstance) => {
    if (!card.userName || !card.userPhone || !card.userAddress) {
      message.warning('该卡牌缺少完整的地址信息');
      return;
    }
    
    const addressText = `${card.userName} ${card.userPhone} ${card.userAddress}`;
    
    try {
      await navigator.clipboard.writeText(addressText);
      message.success('地址已复制到剪贴板');
    } catch (error) {
      // 兼容性处理
      const textArea = document.createElement('textarea');
      textArea.value = addressText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('地址已复制到剪贴板');
    }
  };

  const batchGenerateInstances = async (values: any) => {
    setLoading(true);
    try {
      const endpoint = batchGenerateType === 'evolution' 
        ? '/api/v1/ai-shanhaijing/evolution-card-types/batch-generate'
        : '/api/v1/ai-shanhaijing/oc-cards/batch-generate';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: values.quantity })
      });

      const data = await response.json();
      if (data.success) {
        message.success('批量生成成功');
        setBatchGenerateModalVisible(false);
        batchGenerateForm.resetFields();
        
        // 队列下载TXT文件，避免浏览器下载数量限制
        const fileData = data.data.fileData;
        const downloadQueue = async (files: { code: string, content: string }[]) => {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            // 添加UTF-8 BOM标记（使用与单个下载一致的方式）
            const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
            const blob = new Blob([BOM, file.content], { type: 'text/plain;charset=utf-8' });
            const fileName = `code_${file.code}.txt`;
            saveAs(blob, fileName);
            
            // 每个文件之间间隔500ms，避免浏览器限制
            if (i < files.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        };
        
        downloadQueue(fileData);
        
        // 刷新数据
        if (batchGenerateType === 'evolution') {
          fetchEvolutionCardTypes();
        } else {
          fetchOcCards();
        }
      } else {
        message.error(data.message || '批量生成失败');
      }
    } catch (error) {
      message.error('批量生成失败');
    } finally {
      setLoading(false);
    }
  };

  const createEvolutionCardType = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/evolution-card-types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardImage: values.cardImage
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success('创建进化卡种类成功');
        setEvolutionTypeModalVisible(false);
        evolutionTypeForm.resetFields();
        fetchEvolutionCardTypes();
      } else {
        message.error(data.message || '创建进化卡种类失败');
      }
    } catch (error) {
      message.error('创建进化卡种类失败');
    } finally {
      setLoading(false);
    }
  };

  const generateEvolutionInstances = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ai-shanhaijing/evolution-card-types/${selectedEvolutionType?.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: values.quantity
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个进化卡实例`);
        setGenerateEvolutionModalVisible(false);
        generateEvolutionForm.resetFields();
        fetchEvolutionCardTypes();
        
        // 导出hash列表
        if (data.data && data.data.instances && data.data.instances.length > 0) {
          exportHashList(data.data.instances, 'evolution-card-instances', data.data.code);
        }
      } else {
        message.error(data.message || '生成进化卡实例失败');
      }
    } catch (error) {
      message.error('生成进化卡实例失败');
    } finally {
      setLoading(false);
    }
  };

  const createVote = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/votes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startTime: values.startTime,
          endTime: values.endTime,
          currentStage: values.currentStage
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success('创建投票成功');
        setVoteModalVisible(false);
        voteForm.resetFields();
        fetchVotes();
      } else {
        message.error(data.message || '创建投票失败');
      }
    } catch (error) {
      message.error('创建投票失败');
    } finally {
      setLoading(false);
    }
  };

  const setChampion = async (values: any) => {
    if (!uploadedImageFile) {
      message.error('请先上传成品图片');
      return;
    }

    setLoading(true);
    try {
      // 创建FormData来上传文件
      const formData = new FormData();
      formData.append('candidateId', values.candidateId.toString());
      formData.append('finalImage', uploadedImageFile);

      const response = await fetch(`/api/v1/ai-shanhaijing/votes/${selectedVote?.id}/categories/${selectedCategory}/groups/${selectedGroup}/champion`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        message.success(`设置冠军成功！已为 ${data.voterCount || 0} 位投票者分配强化等级。`);
        setChampionModalVisible(false);
        championForm.resetFields();
        setUploadedImageFile(null);
        if (selectedVote) {
          fetchVoteCategoriesGroups(selectedVote.id);
        }
      } else {
        message.error(data.message || '设置冠军失败');
      }
    } catch (error) {
      message.error('设置冠军失败');
    } finally {
      setLoading(false);
    }
  };

  const closeVote = async (voteId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ai-shanhaijing/votes/${voteId}/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      if (data.success) {
        message.success('投票已成功关闭');
        fetchVotes(); // 刷新投票列表
      } else {
        message.error(data.message || '关闭投票失败');
      }
    } catch (error) {
      message.error('关闭投票失败');
    } finally {
      setLoading(false);
    }
  };

  const createOcCard = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-shanhaijing/oc-cards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          backgroundImage: values.backgroundImage,
          prototypeSketch: values.prototypeSketch,
          prototypeImage: values.prototypeImage
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success('创建OC卡成功');
        setOcCardModalVisible(false);
        ocCardForm.resetFields();
        fetchOcCards();
      } else {
        message.error(data.message || '创建OC卡失败');
      }
    } catch (error) {
      message.error('创建OC卡失败');
    } finally {
      setLoading(false);
    }
  };

  const generateOcInstances = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ai-shanhaijing/oc-cards/${selectedOcCard?.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: values.quantity
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个OC卡实例`);
        setGenerateOcModalVisible(false);
        generateOcForm.resetFields();
        fetchOcCards();
        
        // 导出hash列表
        if (data.data && data.data.instances && data.data.instances.length > 0) {
          exportHashList(data.data.instances, 'oc-card-instances', data.data.code);
        }
      } else {
        message.error(data.message || '生成OC卡实例失败');
      }
    } catch (error) {
      message.error('生成OC卡实例失败');
    } finally {
      setLoading(false);
    }
  };

  const exportHashList = (instances: any[], type: string, code?: string) => {
    const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
    let urlPrefix = '';
    let fileName = '';
    
    if (type === 'evolution-card-instances') {
      urlPrefix = `${BASE_URL}/a/`;
      // 如果有编码则使用编码，否则使用时间戳
      fileName = code ? `code_${code}.txt` : `evolution_cards_${timestamp}.txt`;
    } else if (type === 'oc-card-instances') {
      urlPrefix = `${BASE_URL}/b/`;
      // 如果有编码则使用编码，否则使用时间戳
      fileName = code ? `code_${code}.txt` : `oc_cards_${timestamp}.txt`;
    }
    
    const content = instances.map(instance => `${urlPrefix}${instance.hash}`).join('\r\n') + '\r\n';
    
    // 添加 UTF-8 BOM 标记
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, fileName);
  };

  const evolutionTypeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '编码',
      dataIndex: 'code',
      key: 'code',
      width: 80,
    },
    {
      title: '卡牌图',
      dataIndex: 'cardImage',
      key: 'cardImage',
      render: (image: string) => (
        image ? <img src={image} alt="卡牌图" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : '无'
      ),
    },
    {
      title: '实例数量',
      key: 'instanceCount',
      render: (record: EvolutionCardType) => (
        <Text>{record.instances?.length || 0}</Text>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: EvolutionCardType) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedEvolutionType(record);
              setGenerateEvolutionModalVisible(true);
            }}
          >
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  const voteColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '当前阶段',
      dataIndex: 'currentStage',
      key: 'currentStage',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: 'active' | 'closed') => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '活跃' : '已关闭'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: Vote) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedVote(record);
              fetchVoteCategoriesGroups(record.id);
              setVoteCategoriesModalVisible(true);
            }}
          >
            查看分类和组
          </Button>
          {record.status === 'active' && (
            <Button
              type="primary"
              danger
              icon={<CloseOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: '确认关闭投票',
                  content: `确定要关闭投票 ${record.id} 吗？此操作不可撤销。`,
                  okText: '确定关闭',
                  cancelText: '取消',
                  onOk: () => closeVote(record.id),
                });
              }}
            >
              关闭投票
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const candidateColumns = [
    {
      title: '组号',
      dataIndex: 'group',
      key: 'group',
      width: 80,
    },
    {
      title: '候选人图片',
      key: 'candidateImage',
      render: (record: any) => (
        record.candidate?.aiGeneratedImage ? 
          <img src={record.candidate.aiGeneratedImage} alt="候选人" style={{ width: 60, height: 60, objectFit: 'cover' }} /> : 
          '无图片'
      ),
    },
    {
      title: '候选人Hash',
      key: 'candidateHash',
      render: (record: any) => record.candidate?.hash || '-',
    },
    {
      title: '得票数',
      key: 'voteCount',
      render: (record: any) => record.voters?.length || 0,
    },
    {
      title: '抖音链接',
      key: 'douyinUrl',
      render: (record: any) => (
        record.hasDouyin && record.douyinUrl ? 
          <a href={record.douyinUrl} target="_blank" rel="noopener noreferrer">查看</a> : 
          '无'
      ),
    },
    {
      title: '是否获胜',
      key: 'isWinner',
      render: (record: any) => record.isWinner ? '✅ 冠军' : '❌',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: any) => (
        <Space size="middle">
          {!record.isWinner && (
            <Button
              type="primary"
              icon={<TrophyOutlined />}
              onClick={() => {
                setSelectedGroup(record.group);
                championForm.setFieldsValue({ candidateId: record.id });
                setChampionModalVisible(true);
              }}
            >
              设为冠军
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const ocCardColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '编码',
      dataIndex: 'code',
      key: 'code',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '背景图',
      dataIndex: 'backgroundImage',
      key: 'backgroundImage',
      render: (image: string) => (
        image ? <img src={image} alt="背景图" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : '无'
      ),
    },
    {
      title: '实例数量',
      key: 'instanceCount',
      render: (record: OcCard) => (
        <Text>{record.instances?.length || 0}</Text>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: OcCard) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedOcCard(record);
              setGenerateOcModalVisible(true);
            }}
          >
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  // 获胜卡牌列配置
  const winnerCardColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '卡牌图片',
      dataIndex: 'aiGeneratedImage',
      key: 'aiGeneratedImage',
      render: (image: string) => (
        image ? <img src={image} alt="卡牌图片" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : '无'
      ),
    },
    {
      title: '中文名',
      dataIndex: 'chineseName',
      key: 'chineseName',
    },
    {
      title: '意大利名',
      dataIndex: 'italianName',
      key: 'italianName',
    },
    {
      title: '原型',
      key: 'prototype',
      render: (record: OcCardInstance) => (
        <Text>{record.prototype?.name || '-'}</Text>
      ),
    },
    {
      title: '收货人信息',
      key: 'address',
      render: (record: OcCardInstance) => {
        const hasAddress = record.userName && record.userPhone && record.userAddress;
        return hasAddress ? (
          <div>
            <div>{record.userName} {record.userPhone}</div>
            <div>{record.userAddress}</div>
          </div>
        ) : (
          <Text type="warning">未填写</Text>
        );
      },
    },
    {
      title: '发货状态',
      key: 'shippingStatus',
      render: (record: OcCardInstance) => {
        if (!record.expressTrackingNumber) {
          return <Tag color="orange">未发货</Tag>;
        }
        return <Tag color="green">已发货</Tag>;
      },
    },
    {
      title: '物流单号',
      dataIndex: 'expressTrackingNumber',
      key: 'expressTrackingNumber',
      render: (trackingNumber: string) => trackingNumber || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: OcCardInstance) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyAddressToClipboard(record)}
            disabled={!record.userName || !record.userPhone || !record.userAddress}
          >
            复制地址
          </Button>
          <Button
            type="default"
            size="small"
            icon={<SendOutlined />}
            onClick={() => {
              setSelectedWinnerCard(record);
              setShippingModalVisible(true);
              shippingForm.setFieldsValue({
                expressTrackingNumber: record.expressTrackingNumber || ''
              });
            }}
          >
            发货
          </Button>
        </Space>
      ),
    },
  ];

  // 根据筛选条件过滤获胜卡牌
  const getFilteredWinnerCards = () => {
    switch (winnerCardFilter) {
      case 'shipped':
        return winnerCards.filter(card => card.expressTrackingNumber);
      case 'unshipped':
        return winnerCards.filter(card => !card.expressTrackingNumber);
      default:
        return winnerCards;
    }
  };

  const tabItems = [
    {
      key: 'evolution-cards',
      label: '进化卡管理',
      children: (
        <Card>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setEvolutionTypeModalVisible(true)}
              >
                新增进化卡种类
              </Button>
            </Col>
            <Col>
              <Button
                type="default"
                onClick={() => {
                  setBatchGenerateType('evolution');
                  setBatchGenerateModalVisible(true);
                }}
              >
                批量生成
              </Button>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchEvolutionCardTypes}
                loading={loading}
              >
                刷新数据
              </Button>
            </Col>
          </Row>

          <Table
            dataSource={evolutionCardTypes}
            columns={evolutionTypeColumns}
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: false,
              showQuickJumper: false
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
            bordered={false}
            showHeader={true}
          />
        </Card>
      ),
    },
    {
      key: 'votes',
      label: '投票管理',
      children: (
        <Card>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setVoteModalVisible(true)}
              >
                新增投票
              </Button>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchVotes}
                loading={loading}
              >
                刷新数据
              </Button>
            </Col>
          </Row>

          <Table
            dataSource={votes}
            columns={voteColumns}
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: false,
              showQuickJumper: false
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
            bordered={false}
            showHeader={true}
          />

        </Card>
      ),
    },
    {
      key: 'oc-cards',
      label: 'OC卡管理',
      children: (
        <Card>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setOcCardModalVisible(true)}
              >
                新增OC卡
              </Button>
            </Col>
            <Col>
              <Button
                type="default"
                onClick={() => {
                  setBatchGenerateType('oc');
                  setBatchGenerateModalVisible(true);
                }}
              >
                批量生成
              </Button>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchOcCards}
                loading={loading}
              >
                刷新数据
              </Button>
            </Col>
          </Row>

          <Table
            dataSource={ocCards}
            columns={ocCardColumns}
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: false,
              showQuickJumper: false
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
            bordered={false}
            showHeader={true}
          />
        </Card>
      ),
    },
    {
      key: 'winner-cards',
      label: '获胜卡牌',
      children: (
        <Card>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Button
                  type={winnerCardFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => setWinnerCardFilter('all')}
                >
                  全部 ({winnerCards.length})
                </Button>
                <Button
                  type={winnerCardFilter === 'unshipped' ? 'primary' : 'default'}
                  onClick={() => setWinnerCardFilter('unshipped')}
                >
                  未发货 ({winnerCards.filter(card => !card.expressTrackingNumber).length})
                </Button>
                <Button
                  type={winnerCardFilter === 'shipped' ? 'primary' : 'default'}
                  onClick={() => setWinnerCardFilter('shipped')}
                >
                  已发货 ({winnerCards.filter(card => card.expressTrackingNumber).length})
                </Button>
              </Space>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchWinnerCards}
                loading={loading}
              >
                刷新数据
              </Button>
            </Col>
          </Row>

          <Table
            dataSource={getFilteredWinnerCards()}
            columns={winnerCardColumns}
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: false,
              showQuickJumper: false
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
            bordered={false}
            showHeader={true}
          />
        </Card>
      ),
    },
  ];

  return (
    <div className="ai-shanhaijing-admin">
      <Title level={2}>AI山海经管理</Title>

      <Tabs 
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      {/* 新增进化卡种类模态框 */}
      <Modal
        title="新增进化卡种类"
        open={evolutionTypeModalVisible}
        onCancel={() => {
          setEvolutionTypeModalVisible(false);
          evolutionTypeForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={evolutionTypeForm}
          layout="vertical"
          onFinish={createEvolutionCardType}
        >
          <Form.Item
            name="cardImage"
            label="卡牌图URL"
          >
            <Input placeholder="请输入卡牌图URL（可选）" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                创建
              </Button>
              <Button
                onClick={() => {
                  setEvolutionTypeModalVisible(false);
                  evolutionTypeForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 生成进化卡实例模态框 */}
      <Modal
        title={`生成进化卡实例 - ID: ${selectedEvolutionType?.id}`}
        open={generateEvolutionModalVisible}
        onCancel={() => {
          setGenerateEvolutionModalVisible(false);
          generateEvolutionForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={generateEvolutionForm}
          layout="vertical"
          onFinish={generateEvolutionInstances}
        >
          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={1000} placeholder="请输入生成数量" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                生成并导出
              </Button>
              <Button
                onClick={() => {
                  setGenerateEvolutionModalVisible(false);
                  generateEvolutionForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增投票模态框 */}
      <Modal
        title="新增投票"
        open={voteModalVisible}
        onCancel={() => {
          setVoteModalVisible(false);
          voteForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={voteForm}
          layout="vertical"
          onFinish={createVote}
        >
          <Form.Item
            name="startTime"
            label="投票开始时间"
            rules={[{ required: true, message: '请输入投票开始时间' }]}
          >
            <Input
              type="datetime-local"
              style={{ width: '100%' }}
              placeholder="请选择开始时间"
            />
          </Form.Item>

          <Form.Item
            name="endTime"
            label="投票结束时间"
            rules={[{ required: true, message: '请输入投票结束时间' }]}
          >
            <Input
              type="datetime-local"
              style={{ width: '100%' }}
              placeholder="请选择结束时间"
            />
          </Form.Item>

          <Form.Item
            name="currentStage"
            label="当前阶段"
            rules={[{ required: true, message: '请输入当前阶段' }]}
          >
            <InputNumber min={1} placeholder="请输入当前阶段" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                创建
              </Button>
              <Button
                onClick={() => {
                  setVoteModalVisible(false);
                  voteForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增OC卡模态框 */}
      <Modal
        title="新增OC卡"
        open={ocCardModalVisible}
        onCancel={() => {
          setOcCardModalVisible(false);
          ocCardForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={ocCardForm}
          layout="vertical"
          onFinish={createOcCard}
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="请输入OC卡名称" />
          </Form.Item>

          <Form.Item
            name="backgroundImage"
            label="背景图URL"
          >
            <Input placeholder="请输入背景图URL（可选）" />
          </Form.Item>

          <Form.Item
            name="prototypeSketch"
            label="原型草图URL"
          >
            <Input placeholder="请输入原型草图URL（可选）" />
          </Form.Item>

          <Form.Item
            name="prototypeImage"
            label="原型图URL"
          >
            <Input placeholder="请输入原型图URL（可选）" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                创建
              </Button>
              <Button
                onClick={() => {
                  setOcCardModalVisible(false);
                  ocCardForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 生成OC卡实例模态框 */}
      <Modal
        title={`生成OC卡实例 - ${selectedOcCard?.name}`}
        open={generateOcModalVisible}
        onCancel={() => {
          setGenerateOcModalVisible(false);
          generateOcForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={generateOcForm}
          layout="vertical"
          onFinish={generateOcInstances}
        >
          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={1000} placeholder="请输入生成数量" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                生成并导出
              </Button>
              <Button
                onClick={() => {
                  setGenerateOcModalVisible(false);
                  generateOcForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 设置冠军模态框 */}
      <Modal
        title={`设置类别 ${selectedCategory} 第 ${selectedGroup} 组冠军`}
        open={championModalVisible}
        onCancel={() => {
          setChampionModalVisible(false);
          championForm.resetFields();
          setUploadedImageFile(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={championForm}
          layout="vertical"
          onFinish={setChampion}
        >
          <Form.Item
            name="candidateId"
            label="候选人ID"
            rules={[{ required: true, message: '候选人ID不能为空' }]}
          >
            <InputNumber 
              disabled 
              style={{ width: '100%' }}
              placeholder="候选人ID"
            />
          </Form.Item>

          <Form.Item
            label="成品图片"
            rules={[{ required: true, message: '请上传成品图片' }]}
          >
            <Upload
              name="finalImage"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={(file) => {
                // 检查文件类型
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('只能上传图片文件!');
                  return false;
                }
                
                // 检查文件大小 (10MB)
                const isLt10M = file.size / 1024 / 1024 < 10;
                if (!isLt10M) {
                  message.error('图片大小不能超过10MB!');
                  return false;
                }

                setUploadedImageFile(file);
                message.success('图片选择成功');
                return false; // 阻止自动上传
              }}
              onRemove={() => {
                setUploadedImageFile(null);
              }}
            >
              {uploadedImageFile ? (
                <img 
                  src={URL.createObjectURL(uploadedImageFile)} 
                  alt="成品图片" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传成品图片</div>
                </div>
              )}
            </Upload>
            {uploadedImageFile && (
              <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                已选择文件：{uploadedImageFile.name}
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                设置冠军并分配强化
              </Button>
              <Button
                onClick={() => {
                  setChampionModalVisible(false);
                  championForm.resetFields();
                  setUploadedImageFile(null);
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 投票分类和组查看模态框 */}
      <Modal
        title={selectedVote ? `投票 ${selectedVote.id} 的分类和组` : '投票分类和组'}
        open={voteCategoriesModalVisible}
        onCancel={() => {
          setVoteCategoriesModalVisible(false);
          setSelectedVote(null);
          setVoteCategoriesGroups([]);
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {voteCategoriesGroups.length > 0 && (
            <>
              {voteCategoriesGroups.map(category => (
                <div key={category.categoryId} style={{ marginBottom: 30 }}>
                  <Title level={3}>类别：{category.categoryName}</Title>
                  {category.groups.map((group: any) => (
                    <div key={`${category.categoryId}-${group.groupNumber}`} style={{ marginBottom: 20, marginLeft: 20 }}>
                      <Title level={4}>第 {group.groupNumber} 组</Title>
                      <Table
                        dataSource={group.candidates.map((candidate: any) => ({
                          ...candidate,
                          categoryId: category.categoryId,
                          groupNumber: group.groupNumber
                        }))}
                        columns={[
                          {
                            title: '候选人图片',
                            key: 'candidateImage',
                            render: (record: any) => (
                              record.candidateImage ? 
                                <img src={record.candidateImage} alt="候选人" style={{ width: 60, height: 60, objectFit: 'cover' }} /> : 
                                '无图片'
                            ),
                          },
                          {
                            title: '候选人Hash',
                            key: 'candidateHash',
                            dataIndex: 'candidateHash',
                          },
                          {
                            title: '得票数',
                            key: 'voterCount',
                            dataIndex: 'voterCount',
                          },
                          {
                            title: '抖音链接',
                            key: 'douyinUrl',
                            render: (record: any) => (
                              record.hasDouyin && record.douyinUrl ? 
                                <a href={record.douyinUrl} target="_blank" rel="noopener noreferrer">查看</a> : 
                                '无'
                            ),
                          },
                          {
                            title: '状态',
                            key: 'voteStatus',
                            render: (record: any) => {
                              switch (record.voteStatus) {
                                case 'winner':
                                  return '✅ 获胜';
                                case 'loser':
                                  return '❌ 失败';
                                case 'pending':
                                default:
                                  return '⏳ 待定';
                              }
                            },
                          },
                          {
                            title: '操作',
                            key: 'action',
                            render: (record: any) => (
                              <Space size="middle">
                                {record.voteStatus !== 'winner' && (
                                  <Button
                                    type="primary"
                                    icon={<TrophyOutlined />}
                                    onClick={() => {
                                      setSelectedCategory(record.categoryId);
                                      setSelectedGroup(record.groupNumber);
                                      championForm.setFieldsValue({ candidateId: record.id });
                                      setChampionModalVisible(true);
                                    }}
                                  >
                                    设为冠军
                                  </Button>
                                )}
                              </Space>
                            ),
                          },
                        ]}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        size="small"
                        bordered={true}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
          
          {voteCategoriesGroups.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>暂无分类和组数据</p>
            </div>
          )}
        </div>
      </Modal>

      {/* 发货状态更新模态框 */}
      <Modal
        title="更新发货状态"
        open={shippingModalVisible}
        onCancel={() => {
          setShippingModalVisible(false);
          shippingForm.resetFields();
          setSelectedWinnerCard(null);
        }}
        footer={null}
      >
        <Form
          form={shippingForm}
          layout="vertical"
          onFinish={(values) => {
            if (selectedWinnerCard) {
              updateShippingStatus(selectedWinnerCard.id, values.expressTrackingNumber || undefined);
            }
          }}
        >
          {selectedWinnerCard && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>卡牌信息</Title>
              <p><strong>中文名:</strong> {selectedWinnerCard.chineseName}</p>
              <p><strong>意大利名:</strong> {selectedWinnerCard.italianName}</p>
              <p><strong>收货人:</strong> {selectedWinnerCard.userName || '未填写'}</p>
              <p><strong>电话:</strong> {selectedWinnerCard.userPhone || '未填写'}</p>
              <p><strong>地址:</strong> {selectedWinnerCard.userAddress || '未填写'}</p>
            </div>
          )}

          <Form.Item
            name="expressTrackingNumber"
            label="物流单号"
            help="留空则设置为「无需物流」"
          >
            <Input placeholder="请输入物流单号，留空表示无需物流" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                确认发货
              </Button>
              <Button
                onClick={() => {
                  setShippingModalVisible(false);
                  shippingForm.resetFields();
                  setSelectedWinnerCard(null);
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量生成模态框 */}
      <Modal
        title={`批量生成${batchGenerateType === 'evolution' ? '进化卡' : 'OC卡'}实例`}
        open={batchGenerateModalVisible}
        onCancel={() => {
          setBatchGenerateModalVisible(false);
          batchGenerateForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={batchGenerateForm}
          layout="vertical"
          onFinish={batchGenerateInstances}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              将为所有{batchGenerateType === 'evolution' ? '进化卡种类' : 'OC卡种类'}各生成指定数量的实例，
              并自动下载多个TXT文件，每个编码对应一个文件（如：code_001.txt、code_002.txt），
              文件内容为该编码的所有扫码地址，末尾包含一个空行。
            </Text>
          </div>

          <Form.Item
            name="quantity"
            label="每种卡生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber 
              min={1} 
              max={1000} 
              placeholder="请输入每种卡生成的数量" 
              style={{ width: '100%' }} 
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                开始批量生成
              </Button>
              <Button
                onClick={() => {
                  setBatchGenerateModalVisible(false);
                  batchGenerateForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AiShanhaijingAdmin;